import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Avatar,
  FileInput,
  Select,
  Divider,
  Switch,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconUpload, IconUser, IconMail } from '@tabler/icons-react';
import { User, Profession, Grade, CompetencyMap } from '../../types';
import { createEnterKeyHandler } from '../../utils/enterKeyHandler';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useUserStore } from '../../store/userStore';
import { usePermission } from '../../hooks/usePermission';
import { useCompanyModules } from '../../hooks/useCompanyModules';

interface UserEditModalProps {
  opened: boolean;
  onClose: () => void;
  userData?: User;
  /** Отклоняется при ошибке — тогда окно не закрывается. */
  onSave: (formData: FormData) => Promise<unknown>;
  isEdit?: boolean;
}

export const UserEditModal: React.FC<UserEditModalProps> = ({
  opened,
  onClose,
  userData,
  onSave,
  isEdit,
}) => {
  const { t } = useTranslation();
  const meId = useUserStore((s) => s.user?._id);
  const isSelf = !!userData?._id && userData._id === meId;
  // Флаги ролей выдаёт держатель company.roles, грейд меняет — people.grade.change (как на бэкенде).
  const canGrantRoles = usePermission('company.roles');
  // Пометки людей — по подключённым компании разделам, а не по своему доступу к ним.
  const { hasCompanyModule } = useCompanyModules();
  const canChangeGrade = usePermission('people.grade.change');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    position: '',
    active: true,
    admin: false,
    avatar: '',
    profession: '',
    grade: '',
    essaysReviewer: false,
    quarterlyReviewer: false,
    scoreObserver: false,
    scoreManualOnly: false,
    isEssayExempt: false,
    essaysEditor: false,
    reviewObserver: false,
  });
  const [hireDate, setHireDate] = useState<Date | null>(null);
  const [dismissedAt, setDismissedAt] = useState<Date | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [isLoadingProfessions, setLoadingProfessions] = useState(false);
  const [isUploading, setUploading] = useState(false);
  const [grades, setGrades] = useState<Grade[]>([]);

  useEffect(() => {
    const loadProfessions = async () => {
      if (opened) {
        setLoadingProfessions(true);
        try {
          const data: Profession[] = await api('/professions');
          setProfessions(data);
        } catch (error) {
          console.error('Error loading professions:', error);
        } finally {
          setLoadingProfessions(false);
        }
      }
    };
    loadProfessions();
  }, [opened]);

  useEffect(() => {
    if (userData) {
      setFormData({
        name: userData.name || '',
        email: userData.email || '',
        position: userData.position || '',
        // `|| true` делал неактивного активным: сохранение формы его реактивировало.
        active: userData.active !== false,
        admin: userData.admin === true,
        avatar: userData.avatar || '',
        profession: userData.profession?._id || '',
        grade: userData.grade?._id || '',
        essaysReviewer: userData.essaysReviewer === true,
        quarterlyReviewer: userData.quarterlyReviewer === true,
        scoreObserver: userData.scoreObserver === true,
        scoreManualOnly: userData.scoreManualOnly === true,
        isEssayExempt: userData.isEssayExempt === true,
        essaysEditor: userData.essaysEditor === true,
        reviewObserver: userData.reviewObserver === true,
      });
      setHireDate(userData.hireDate ? new Date(userData.hireDate) : null);
      setDismissedAt(userData.dismissedAt ? new Date(userData.dismissedAt) : null);
      setAvatarFile(null);
    } else {
      setFormData({
        name: '',
        email: '',
        position: '',
        active: true,
        admin: false,
        avatar: '',
        profession: '',
        grade: '',
        essaysReviewer: false,
        quarterlyReviewer: false,
        scoreObserver: false,
        scoreManualOnly: false,
        isEssayExempt: false,
        essaysEditor: false,
        reviewObserver: false,
      });
      setHireDate(null);
      setAvatarFile(null);
    }
  }, [userData, opened]);

  useEffect(() => {
    // Загружаем грейды для выбранной профессии
    const loadGrades = async () => {
      if (formData.profession) {
        try {
          const maps: CompetencyMap[] = await api('/competency-maps');
          const map = maps.find((m) =>
            (m.professions || []).some((p) => p._id === formData.profession),
          );
          setGrades(map?.grades || []);
        } catch (error) {
          setGrades([]);
        }
      } else {
        setGrades([]);
      }
    };
    loadGrades();
  }, [formData.profession, opened]);

  const handleSave = async () => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('email', formData.email);
      fd.append('position', formData.position);
      fd.append('active', String(formData.active));
      fd.append('profession', formData.profession);
      fd.append('grade', formData.grade);
      fd.append('scoreManualOnly', String(formData.scoreManualOnly));
      fd.append('isEssayExempt', String(formData.isEssayExempt));
      if (canGrantRoles) {
        fd.append('admin', String(formData.admin));
        fd.append('essaysReviewer', String(formData.essaysReviewer));
        fd.append('quarterlyReviewer', String(formData.quarterlyReviewer));
        fd.append('scoreObserver', String(formData.scoreObserver));
        fd.append('essaysEditor', String(formData.essaysEditor));
        fd.append('reviewObserver', String(formData.reviewObserver));
      }
      fd.append('hireDate', hireDate ? hireDate.toISOString() : '');
      // Без даты сервер поставит сегодня; работающему дата не нужна — сервер её снимет.
      if (!formData.active && dismissedAt) fd.append('dismissedAt', dismissedAt.toISOString());
      if (avatarFile) fd.append('avatar', avatarFile);
      await onSave(fd);
      onClose();
    } catch {
      // Ошибку уже показал onSave; окно остаётся открытым, введённое не теряется.
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={isEdit ? t('users.editUser') : t('users.createUser')}
      size="lg"
      centered
      onKeyDown={createEnterKeyHandler({ onSubmit: handleSave })}
    >
      <Stack gap="lg">
        {/* Аватар */}
        <Group gap="lg" align="center">
          <Avatar
            src={avatarFile ? URL.createObjectURL(avatarFile) : formData.avatar}
            size={80}
            radius="xl"
          />
          <Stack gap="xs" style={{ flex: 1 }}>
            <span style={{ fontSize: 14, fontWeight: 500 }}>{t('users.userPhoto')}</span>
            <FileInput
              placeholder={t('users.selectFile')}
              accept="image/*"
              leftSection={<IconUpload size={16} />}
              value={avatarFile}
              onChange={setAvatarFile}
              size="sm"
            />
          </Stack>
        </Group>

        <Divider />

        {/* Основная информация */}
        <Stack gap="md">
          <TextInput
            label={t('users.name')}
            placeholder={t('users.enterName')}
            leftSection={<IconUser size={16} />}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <TextInput
            label={t('users.email')}
            placeholder={t('users.enterEmail')}
            leftSection={<IconMail size={16} />}
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />

          <TextInput
            label={t('users.position')}
            placeholder={t('users.enterPosition')}
            leftSection={<IconUser size={16} />}
            value={formData.position}
            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
          />

          <Select
            label={t('users.profession')}
            placeholder={
              isLoadingProfessions ? t('users.isLoadingProfessions') : t('users.selectProfession')
            }
            data={professions.map((p) => ({ value: p._id, label: p.name }))}
            value={formData.profession}
            onChange={(value) => setFormData({ ...formData, profession: value || '' })}
            clearable
            disabled={isLoadingProfessions}
          />

          <Select
            label={t('users.grade')}
            placeholder={
              grades.length === 0 ? t('users.selectProfessionFirst') : t('users.selectGrade')
            }
            data={grades.map((g) => ({ value: g._id, label: g.title }))}
            value={formData.grade || ''}
            onChange={(value) => setFormData({ ...formData, grade: value || '' })}
            clearable
            disabled={grades.length === 0 || (isEdit && !canChangeGrade)}
          />

          <DateInput
            label={t('users.hireDate')}
            placeholder={t('users.hireDatePlaceholder')}
            value={hireDate}
            onChange={(value) => setHireDate(value ? new Date(value) : null)}
            valueFormat="DD.MM.YYYY"
            clearable
          />

          <Select
            label={t('users.status')}
            placeholder={t('users.selectStatus')}
            data={[
              { value: 'true', label: t('users.active') },
              { value: 'false', label: t('users.inactive') },
            ]}
            value={formData.active.toString()}
            onChange={(value) => {
              const isActive = value === 'true';
              setFormData({ ...formData, active: isActive });
              if (!isActive && !dismissedAt) setDismissedAt(new Date());
            }}
            description={formData.active ? undefined : t('users.archiveHint')}
          />

          {!formData.active && (
            <DateInput
              label={t('users.dismissedAt')}
              placeholder={t('users.hireDatePlaceholder')}
              value={dismissedAt}
              onChange={(value) => setDismissedAt(value ? new Date(value) : null)}
              valueFormat="DD.MM.YYYY"
            />
          )}

          {hasCompanyModule('quarterly') && (
            <Switch
              color="teal"
              checked={formData.scoreManualOnly}
              onChange={(event) =>
                setFormData({ ...formData, scoreManualOnly: event.currentTarget.checked })
              }
              label={t('users.scoreManualOnly')}
              description={t('users.scoreManualOnlyDescription')}
            />
          )}

          {hasCompanyModule('essays') && (
            <Switch
              color="teal"
              checked={formData.isEssayExempt}
              onChange={(event) =>
                setFormData({ ...formData, isEssayExempt: event.currentTarget.checked })
              }
              label={t('users.isEssayExempt')}
              description={t('users.isEssayExemptDescription')}
            />
          )}

          {canGrantRoles && (
            <>
              {/* Себя разжаловать нельзя: иначе можно оставить организацию без админа. */}
              <Switch
                color="teal"
                checked={formData.admin}
                disabled={isSelf}
                onChange={(event) =>
                  setFormData({ ...formData, admin: event.currentTarget.checked })
                }
                label={t('users.admin')}
                description={t(isSelf ? 'users.adminSelfDescription' : 'users.adminDescription')}
              />

              <Switch
                color="teal"
                checked={formData.essaysReviewer}
                onChange={(event) =>
                  setFormData({ ...formData, essaysReviewer: event.currentTarget.checked })
                }
                label={t('users.essaysReviewer')}
                description={t('users.essaysReviewerDescription')}
              />

              <Switch
                color="teal"
                checked={formData.quarterlyReviewer}
                onChange={(event) =>
                  setFormData({ ...formData, quarterlyReviewer: event.currentTarget.checked })
                }
                label={t('users.quarterlyReviewer')}
                description={t('users.quarterlyReviewerDescription')}
              />

              <Switch
                color="teal"
                checked={formData.scoreObserver}
                onChange={(event) =>
                  setFormData({ ...formData, scoreObserver: event.currentTarget.checked })
                }
                label={t('users.scoreObserver')}
                description={t('users.scoreObserverDescription')}
              />

              <Switch
                color="teal"
                checked={formData.essaysEditor}
                onChange={(event) =>
                  setFormData({ ...formData, essaysEditor: event.currentTarget.checked })
                }
                label={t('users.essaysEditor')}
                description={t('users.essaysEditorDescription')}
              />

              <Switch
                color="teal"
                checked={formData.reviewObserver}
                onChange={(event) =>
                  setFormData({ ...formData, reviewObserver: event.currentTarget.checked })
                }
                label={t('users.reviewObserver')}
                description={t('users.reviewObserverDescription')}
              />
            </>
          )}
        </Stack>

        <Divider />

        {/* Кнопки */}
        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={handleCancel} color="gray">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} color="teal" loading={isUploading}>
            {isEdit ? t('common.save') : t('common.add')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
